import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectCockpitGeoMappingItems } from 'partner360/features/financialCockpit/model/selectors'

/** Short display name for each geo node — what the hierarchy tables' Geo column labels its
 *  `level: 'geo'` rows with, keeping the widest dimension cell narrow. The geo counterpart of
 *  `usePartnerNameById`, resolved the same way.
 *
 *  Keyed by `itemId`, which is exactly what a geo row already carries in `geoKey` (see
 *  `IRevenueAndSobHierarchyNode.geoKey`) — that is why the short name can be resolved here rather
 *  than baked into `geoLabel` by the builder, leaving the full name on the row for the hover title.
 *
 *  Falls back to the full `name` per item: `shortName` is a non-optional string on the payload, but
 *  nothing guarantees it was filled in (nor that it is actually shorter — plenty of nodes carry the
 *  same value in both), so an item without one still reads as something rather than blank. */
export const useGeoShortNameById = (): Map<string, string> => {
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)

  return useMemo(
    () =>
      new Map(geoMappingItems.map(({ itemId, name, shortName }) => [itemId, shortName || name])),
    [geoMappingItems]
  )
}
